import React, { useEffect, useState } from 'react';
import {
    Button,
    Col,
    ConfigProvider,
    DatePicker,
    Descriptions,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import API_ROUTES from '../../../api';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export default function AccessLogManage() {
    const [form] = Form.useForm();
    const [tableLoading, setTableLoading] = useState(false);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [pageData, setPageData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [overview, setOverview] = useState({ pv: 0, uv: 0, ipCount: 0 });
    const [detailRecord, setDetailRecord] = useState(null);

    const buildParams = (page = currentPage, size = pageSize) => {
        const values = form.getFieldsValue();
        const params = {
            current: page,
            size,
            userEmail: values.userEmail,
            userName: values.userName,
            ip: values.ip,
            uri: values.uri,
            method: values.method,
            statusCode: values.statusCode,
            success: values.success
        };

        if (values.timeRange && values.timeRange.length === 2) {
            params.startTime = values.timeRange[0].format('YYYY-MM-DDTHH:mm:ss');
            params.endTime = values.timeRange[1].format('YYYY-MM-DDTHH:mm:ss');
        }

        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        return params;
    };

    const fetchOverview = (params = buildParams()) => {
        setOverviewLoading(true);
        const { current, size, ...overviewParams } = params;
        axios.get(API_ROUTES.api_access_log_overview, {
            params: overviewParams,
            headers: { Authorization: localStorage.getItem('userInfo') }
        })
            .then((response) => {
                if (response.data.code === 200) {
                    setOverview(response.data.data || { pv: 0, uv: 0, ipCount: 0 });
                } else {
                    message.error(response.data.msg || 'Failed to fetch access overview');
                }
            })
            .catch((error) => {
                console.error('Access overview fetch error', error);
                message.error(error.response?.data?.msgDes || 'Failed to fetch access overview');
            })
            .finally(() => {
                setOverviewLoading(false);
            });
    };

    const fetchData = (page = currentPage, size = pageSize) => {
        const params = buildParams(page, size);
        setTableLoading(true);
        axios.get(API_ROUTES.api_access_log_list, {
            params,
            headers: { Authorization: localStorage.getItem('userInfo') }
        })
            .then((response) => {
                if (response.data.code === 200) {
                    setPageData(response.data.data?.records || []);
                    setTotalCount(response.data.data?.total || 0);
                    fetchOverview(params);
                } else {
                    message.error(response.data.msg || 'Failed to fetch access logs');
                    setPageData([]);
                    setTotalCount(0);
                }
            })
            .catch((error) => {
                console.error('Access log fetch error', error);
                message.error(error.response?.data?.msgDes || 'Failed to fetch access logs');
                setPageData([]);
                setTotalCount(0);
            })
            .finally(() => {
                setTableLoading(false);
            });
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchData(1, pageSize);
    };

    const handleReset = () => {
        form.resetFields();
        setCurrentPage(1);
        fetchData(1, pageSize);
    };

    const renderDateTime = (text) => {
        return text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-';
    };

    const formatIpLocation = (ipInfo) => {
        if (!ipInfo) {
            return '-';
        }
        return [ipInfo.country, ipInfo.region, ipInfo.city].filter(Boolean).join(' / ') || '-';
    };

    const columns = [
        {
            title: 'Request Time',
            dataIndex: 'requestTime',
            key: 'requestTime',
            width: 180,
            render: (text) => <Text type="secondary" style={{ fontSize: '12px' }}>{renderDateTime(text)}</Text>
        },
        {
            title: 'User',
            key: 'user',
            width: 240,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ color: '#055878' }}>{record.userEmail || 'Anonymous'}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{record.userName || record.userId || '-'}</Text>
                </Space>
            )
        },
        {
            title: 'IP Address',
            dataIndex: 'ip',
            key: 'ip',
            width: 150,
            ellipsis: true
        },
        {
            title: 'IP Location',
            dataIndex: 'ipInfo',
            key: 'ipInfo',
            width: 250,
            render: (ipInfo) => (
                <Space direction="vertical" size={0} style={{ maxWidth: '100%' }}>
                    <Text style={{ maxWidth: 220 }} ellipsis={{ tooltip: formatIpLocation(ipInfo) }}>
                        {formatIpLocation(ipInfo)}
                    </Text>
                    {ipInfo?.org ? (
                        <Text type="secondary" style={{ fontSize: '12px', maxWidth: 220 }} ellipsis={{ tooltip: ipInfo.org }}>
                            {ipInfo.org}
                        </Text>
                    ) : null}
                    {ipInfo?.status === 2 ? (
                        <Text type="danger" style={{ fontSize: '12px' }}>Lookup failed</Text>
                    ) : null}
                </Space>
            )
        },
        {
            title: 'Method',
            dataIndex: 'method',
            key: 'method',
            width: 100,
            render: (text) => <Tag color={text === 'GET' ? 'blue' : 'purple'}>{text || '-'}</Tag>
        },
        {
            title: 'URI',
            dataIndex: 'uri',
            key: 'uri',
            width: 360,
            ellipsis: true,
            render: (text, record) => (
                <Space direction="vertical" size={0} style={{ maxWidth: '100%' }}>
                    <Text style={{ maxWidth: 330 }} ellipsis={{ tooltip: text }}>{text || '-'}</Text>
                    {record.queryString ? (
                        <Text type="secondary" style={{ fontSize: '12px', maxWidth: 330 }} ellipsis={{ tooltip: record.queryString }}>
                            ?{record.queryString}
                        </Text>
                    ) : null}
                </Space>
            )
        },
        {
            title: 'Status',
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 100,
            render: (text) => {
                const status = Number(text);
                const color = status >= 500 ? 'red' : status >= 400 ? 'orange' : 'green';
                return <Tag color={color}>{text || '-'}</Tag>;
            }
        },
        {
            title: 'Result',
            dataIndex: 'success',
            key: 'success',
            width: 110,
            render: (success) => (
                Number(success) === 1
                    ? <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>
            )
        },
        {
            title: 'Duration',
            dataIndex: 'durationMs',
            key: 'durationMs',
            width: 110,
            render: (text) => <Text>{text == null ? '-' : `${text} ms`}</Text>
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Tooltip title="View Details">
                    <Button type="text" icon={<EyeOutlined />} onClick={() => setDetailRecord(record)} />
                </Tooltip>
            )
        }
    ];

    return (
        <div style={{ background: 'white' }}>
            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={24} sm={8}>
                    <div style={{ padding: '16px', border: '1px solid #e6f0f4', borderRadius: '8px', background: '#f8fbff' }}>
                        <Statistic title="Page Views" value={overview.pv || 0} loading={overviewLoading} prefix={<EyeOutlined />} />
                    </div>
                </Col>
                <Col xs={24} sm={8}>
                    <div style={{ padding: '16px', border: '1px solid #e6f0f4', borderRadius: '8px', background: '#f8fbff' }}>
                        <Statistic title="Unique Visitors" value={overview.uv || 0} loading={overviewLoading} prefix={<UserOutlined />} />
                    </div>
                </Col>
                <Col xs={24} sm={8}>
                    <div style={{ padding: '16px', border: '1px solid #e6f0f4', borderRadius: '8px', background: '#f8fbff' }}>
                        <Statistic title="Unique IPs" value={overview.ipCount || 0} loading={overviewLoading} prefix={<ClockCircleOutlined />} />
                    </div>
                </Col>
            </Row>

            <Form form={form} layout="vertical" style={{ marginBottom: '16px' }}>
                <Row gutter={16}>
                    <Col xs={24} md={8} lg={6}>
                        <Form.Item name="timeRange" label="Request Time">
                            <RangePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <Form.Item name="userEmail" label="User Email">
                            <Input allowClear placeholder="email" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <Form.Item name="userName" label="User Name">
                            <Input allowClear placeholder="name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <Form.Item name="ip" label="IP">
                            <Input allowClear placeholder="IP address" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8} lg={6}>
                        <Form.Item name="uri" label="URI">
                            <Input allowClear placeholder="/search/searchOne" />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Item name="method" label="Method">
                            <Select
                                allowClear
                                placeholder="All"
                                options={['GET', 'POST', 'PUT', 'DELETE'].map((method) => ({ label: method, value: method }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Item name="success" label="Result">
                            <Select
                                allowClear
                                placeholder="All"
                                options={[
                                    { label: 'Success', value: 1 },
                                    { label: 'Failed', value: 0 }
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Item name="statusCode" label="Status Code">
                            <InputNumber min={100} max={599} style={{ width: '100%' }} placeholder="200" />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={6} lg={4}>
                        <Form.Item label=" " colon={false}>
                            <Space>
                                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} style={{ background: '#055878' }}>
                                    Search
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                    Reset
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <ConfigProvider theme={{
                components: {
                    Table: {
                        headerBg: '#f0f4f8',
                        headerColor: '#055878',
                        headerBorderRadius: 8,
                        rowHoverBg: '#f8fbff'
                    }
                }
            }}>
                <Table
                    columns={columns}
                    dataSource={pageData}
                    rowKey="id"
                    loading={tableLoading}
                    pagination={{
                        showQuickJumper: true,
                        showSizeChanger: true,
                        defaultPageSize: 15,
                        current: currentPage,
                        total: totalCount,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                            fetchData(page, size);
                        },
                        style: { marginTop: '24px' }
                    }}
                    scroll={{ x: 1510 }}
                    size="middle"
                    style={{ borderRadius: '8px' }}
                />
            </ConfigProvider>

            <Modal
                title={<span style={{ color: '#055878', fontWeight: 600 }}>Access Log Details</span>}
                open={!!detailRecord}
                onCancel={() => setDetailRecord(null)}
                footer={null}
                width={900}
            >
                {detailRecord ? (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Trace ID">{detailRecord.traceId || '-'}</Descriptions.Item>
                        <Descriptions.Item label="User">{detailRecord.userEmail || 'Anonymous'} / {detailRecord.userName || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP">{detailRecord.ip || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP Location">{formatIpLocation(detailRecord.ipInfo)}</Descriptions.Item>
                        <Descriptions.Item label="IP Organization">{detailRecord.ipInfo?.org || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP Coordinates">{detailRecord.ipInfo?.loc || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP Timezone">{detailRecord.ipInfo?.timezone || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP Postal">{detailRecord.ipInfo?.postal || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP Lookup Status">
                            {detailRecord.ipInfo
                                ? (detailRecord.ipInfo.status === 1 ? 'Success' : `Failed${detailRecord.ipInfo.errorMsg ? `: ${detailRecord.ipInfo.errorMsg}` : ''}`)
                                : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="X-Forwarded-For">{detailRecord.xForwardedFor || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Method / URI">{detailRecord.method || '-'} {detailRecord.uri || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Query String">{detailRecord.queryString || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Request Body">{detailRecord.requestBody || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Handler Method">{detailRecord.handlerMethod || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Status / Duration">{detailRecord.statusCode || '-'} / {detailRecord.durationMs == null ? '-' : `${detailRecord.durationMs} ms`}</Descriptions.Item>
                        <Descriptions.Item label="Request Time">{renderDateTime(detailRecord.requestTime)}</Descriptions.Item>
                        <Descriptions.Item label="Response Time">{renderDateTime(detailRecord.responseTime)}</Descriptions.Item>
                        <Descriptions.Item label="User Agent">{detailRecord.userAgent || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Referer">{detailRecord.referer || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Exception">{detailRecord.exceptionName || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Exception Message">{detailRecord.exceptionMessage || '-'}</Descriptions.Item>
                    </Descriptions>
                ) : null}
            </Modal>
        </div>
    );
}
